import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import { toSlug } from '../../../util/strings';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import ChartTooltip from '../../../components/layout/ChartTooltip';
import SoilingLossFraction from '../../../components/graphs/SoilingLossFraction';
import PrecipitationParticulateMatter from '../../../components/graphs/PrecipitationParticulateMatter';
import MonthlyAverageSoilingLoss from '../../../components/graphs/MonthlyAverageSoilingLoss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn, faGear } from '@fortawesome/free-solid-svg-icons';
import { usePortfolioDateFormatter } from '../../../utils/usePortfolioDateFormatter';
import { apiFetch } from '../../../api/apiClient';
import { mapSoilingMetricsToCards } from './soilingKpis';

function Soiling() {
  const { plant: plantData } = usePlantData();
  const portfolioName = toSlug(plantData.plant_name) ?? "";
  const dateFormatter = usePortfolioDateFormatter();
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [kpiCards, setKpiCards] = useState(() => mapSoilingMetricsToCards(null));

  useEffect(() => {
    document.title = `Vivosol Plant Performance Insights Hub | ${portfolioName} / Insights / Soiling`;
  }, [portfolioName]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!portfolioName) {
        setKpiCards(mapSoilingMetricsToCards(null));
        return;
      }

      try {
        const payload = await apiFetch(
          `/api/soiling/portfolio/${encodeURIComponent(portfolioName)}/metrics`,
        );
        if (!cancelled) {
          setKpiCards(mapSoilingMetricsToCards(payload));
        }
      } catch (err) {
        console.warn('Soiling KPI metrics API fetch failed:', err);
        if (!cancelled) {
          setKpiCards(mapSoilingMetricsToCards(null));
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [portfolioName]);

  return (
    <SidebarPageLayout id="soiling">
      <div className="content p-5">
        <ChartTooltip tooltip={tooltip} transition={false} />
        <Marquee />
        <h1 className="mt-3 mb-0"><Link to={`${plantData.dashboard}`}><strong>{plantData.plant_name}</strong></Link> : {plantData.config} ({plantData.location})</h1>
        <Breadcrumbs labels={{ portfolioName: plantData.plant_name }} />
        <div className="metrics">
          <div className="card">
            <div className="title">
              <span>Current Soiling Loss (HSU)</span>
            </div>
            <div className="value">
              <span>{kpiCards.current.value}</span>
            </div>
            <div className={`footer ${kpiCards.current.footerClass}`.trim()}>
              {kpiCards.current.footer}
            </div>
          </div>
          <div className="card">
            <div className="title">
              <span>Energy Loss, Trailing 30 Day</span>
            </div>
            <div className="value">
              <span>{kpiCards.energy.value}</span>
            </div>
            <div className={`footer ${kpiCards.energy.footerClass}`.trim()}>
              {kpiCards.energy.footer}
            </div>
          </div>
          <div className="card">
            <div className="title">
              <span>Days Since Cleaning Rain</span>
            </div>
            <div className="value">
              <span>{kpiCards.days.value}</span>
            </div>
            <div className={`footer ${kpiCards.days.footerClass}`.trim()}>
              {kpiCards.days.footer}
            </div>
          </div>
          <div className="card">
            <div className="title">
              <span>14-Day Forecast Peak Loss</span>
            </div>
            <div className="value">
              <span>{kpiCards.forecast.value}</span>
            </div>
            <div className={`footer ${kpiCards.forecast.footerClass}`.trim()}>
              {kpiCards.forecast.footer}
            </div>
          </div>
        </div>
        <div id="soilingLossFraction" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3 mb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faChartColumn} /> Soiling Loss Fraction - 90 Day History</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button type="button" aria-label="Settings">
                <FontAwesomeIcon icon={faGear} />
              </button>
            </div>
          </div>
          <SoilingLossFraction
            onTooltip={setTooltip}
            timeZone={dateFormatter.timeZone}
          />
        </div>
        <div id="precipitationParticulateMatter" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3 mb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faChartColumn} /> Precipitation (rain resets) and particulate matter - 90 Day History</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button type="button" aria-label="Settings">
                <FontAwesomeIcon icon={faGear} />
              </button>
            </div>
          </div>
          <PrecipitationParticulateMatter
            onTooltip={setTooltip}
            timeZone={dateFormatter.timeZone}
          />
        </div>
        <div id="monthlyAverageSoilingLoss" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3 mb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faChartColumn} /> Monthly Average Soiling Loss - 10 Year Profile</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button type="button" aria-label="Settings">
                <FontAwesomeIcon icon={faGear} />
              </button>
            </div>
          </div>
          <MonthlyAverageSoilingLoss
            onTooltip={setTooltip}
            portfolioName={portfolioName}
          />
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default Soiling;
